import Avatar from "boring-avatars";
import { Md5 } from "md5-typescript";
import { ReactComponent as GleapBotIcon } from "../../Assets/GleapBotIcon.svg";
import { ReactComponent as ChevronLeft } from "../../Assets/angle-left-solid.svg";
import { ReactComponent as ShareIcon } from "../../Assets/arrow-up-right-from-square-solid.svg";
import { ReactComponent as XIcon } from "../../Assets/x-mark.svg";
import CommunicationManager from "../../Helper/CommunicationManager";
import Communicator from "../../Helper/Communicator";
import {
  buildHelpCenterArticleUrl,
  withSessionIdentity,
} from "../../Helper/HelpCenterUrl";
import { isOnline } from "../../Helper/OnlineState";
import { useAppViewStore } from "../../Stores/AppView";
import { useChecklistStore } from "../../Stores/Checklist";
import { useConfigStore } from "../../Stores/Config";
import { useFeatureRequestStore } from "../../Stores/FeatureRequests";
import { useFormStore } from "../../Stores/Form";
import { useHelpCenterStore } from "../../Stores/HelpCenter";
import { AppMode, useRouterStore } from "../../Stores/Router";
import { useSessionStore } from "../../Stores/Session";
import { useTicketStore } from "../../Stores/Ticket";
import { useSurveyV2Store } from "../../Stores/SurveyV2";
import { AvatarImage, GLEAP_AVATAR_COLORS } from "../AvatarImage/AvatarImage";
import ExpandWidgetButton from "../ExpandWidgetButton/ExpandWidgetButton";
import IconButton from "../IconButton/IconButton";
import { getAiAssistantLabel } from "../../Helper/AiDisclosure";
import "./WidgetHeader.scss";

// When we know who the contact is (name and/or email), render their gravatar
// — gravatar's `?d=initials&name=...` parameter falls back to a colored
// initials circle when no avatar is registered. When we have neither name
// nor email (anonymous guest), render a locally-drawn boring-avatars SVG.
export const ContactAvatar = ({
  email,
  name,
  fallbackSeed,
}: {
  email?: string;
  name?: string;
  fallbackSeed: string;
}) => {
  const hasUserInfo = !!(
    (email && email.length > 0) ||
    (name && name.length > 0)
  );
  if (!hasUserInfo) {
    return (
      <Avatar
        size={64}
        name={fallbackSeed}
        variant="beam"
        colors={GLEAP_AVATAR_COLORS}
      />
    );
  }
  const hash = Md5.init(email ?? name ?? "");
  const initialsName = name || email || "";
  const gravatarUrl = `https://www.gravatar.com/avatar/${hash}?d=initials&name=${encodeURIComponent(initialsName)}`;
  // gravatar can be blocked (ad/privacy blocker, network policy) — fall back
  // to a locally-generated initials avatar so the slot is never broken.
  return <AvatarImage src={gravatarUrl} alt={initialsName} />;
};

// Renders an agent's avatar: real profile image when set, otherwise a
// gravatar/initials circle from their name — an assigned operator without a
// profile picture must still be presented as that operator (previously they
// fell through the info chain entirely).
const agentAvatar = (user: any) => {
  if (user?.profileImageUrl) {
    return <AvatarImage src={user.profileImageUrl} alt={user?.firstName} />;
  }
  return (
    <ContactAvatar
      email={user?.email}
      name={user?.firstName}
      fallbackSeed={user?.email || user?.id || user?._id || user?.firstName}
    />
  );
};

// Resolves who the CUSTOMER is talking to for a ticket: bot → assigned
// operator → last replying agent → team. Never the customer themselves —
// a contact fallback (added 2026-05-06 so unanswered list rows weren't
// blank) presented the customer's own name/avatar in the conversation list,
// headers and chatbar whenever the latest public comment had no agent user
// (bot/workflow comments have none). The team fallback covers that case.
// The AI agent currently fronting the conversation, resolved from its trail in
// the comments: the newest comment that carries a per-message agent identity —
// an agent reply (`data.agentName`) or the passtoagent hand-off carrier
// (`data.action.agentName`). A newer TEAMMATE reply ends the agent's ownership
// of the header, so the walk stops there. Falls back to the ticket's
// `latestPublicComment` when no comment list is available (conversation list
// rows).
const getEngagedAgentIdentity = (
  ticket: any,
  comments?: any[],
): { name: string; avatarUrl?: string } | null => {
  const trail =
    comments && comments.length > 0
      ? comments
      : ticket?.latestPublicComment
        ? [ticket.latestPublicComment]
        : [];
  for (let i = trail.length - 1; i >= 0; i--) {
    const message = trail[i];
    if (message?.user?.firstName) {
      return null;
    }
    if (message?.data?.agentName) {
      return {
        name: message.data.agentName,
        avatarUrl: message.data.agentAvatarUrl,
      };
    }
    if (
      message?.data?.action?.type === "passtoagent" &&
      message?.data?.action?.agentName
    ) {
      return {
        name: message.data.action.agentName,
        avatarUrl: message.data.action.agentAvatarUrl,
      };
    }
  }
  return null;
};

// The frontline AI agent's header identity: the project's operator avatar
// (Settings > Branding > Chatbot avatar) or the default bot mark, plus the
// configured bot name.
const getBotOperatorInfo = (configStore: any, config: any) => {
  const operatorAvatarImage = config?.operatorAvatarImage;
  return {
    image:
      operatorAvatarImage && operatorAvatarImage.length > 0 ? (
        <AvatarImage
          src={operatorAvatarImage}
          fallback={<GleapBotIcon aria-hidden="true" focusable="false" />}
        />
      ) : (
        <GleapBotIcon aria-hidden="true" focusable="false" />
      ),
    name: configStore.getBotName(),
    bot: true,
  };
};

// The header title naming who fronts the conversation. "Enforce EU AI Act":
// an AI agent is marked as one next to its name — never a teammate, whose
// operator info has `bot: false`.
const renderOperatorTitle = (
  configStore: any,
  operatorInfo: { name: string; bot: boolean },
) => {
  if (operatorInfo.bot && configStore.isEuAiActEnforced()) {
    return (
      <div
        className="operator-info-content-title operator-info-content-title--ai"
        role="heading"
        aria-level={1}
      >
        <span className="operator-info-content-name">{operatorInfo.name}</span>
        <span className="operator-info-content-ai-tag">
          {getAiAssistantLabel()}
        </span>
      </div>
    );
  }

  return (
    <div className="operator-info-content-title" role="heading" aria-level={1}>
      {operatorInfo.name}
    </div>
  );
};

export const getOperatorInfo = (
  configStore: any,
  config: any,
  ticket: any,
  comments?: any[],
) => {
  // An engaged AI agent (Kai Resolve / a custom agent) fronts the
  // conversation under its own identity. Custom avatar first, the default
  // bot mark otherwise — never the frontline bot's operator image, so the
  // two AI tiers stay visually distinct.
  const engagedAgent = getEngagedAgentIdentity(ticket, comments);
  if (engagedAgent) {
    return {
      image: engagedAgent.avatarUrl ? (
        <AvatarImage
          src={engagedAgent.avatarUrl}
          fallback={<GleapBotIcon aria-hidden="true" focusable="false" />}
        />
      ) : (
        <GleapBotIcon aria-hidden="true" focusable="false" />
      ),
      name: engagedAgent.name,
      bot: true,
    };
  }

  if (ticket?.bot?.active) {
    return getBotOperatorInfo(configStore, config);
  }

  if (ticket) {
    // `ticketHeaderMode` decides who fronts the conversation once the AI is
    // no longer handling it (project setting, "Conversation header").
    const headerMode = config?.ticketHeaderMode ?? "ai-team";

    // "Show only AI": the AI agent keeps the header even after a hand-off.
    if (headerMode === "ai-only") {
      return getBotOperatorInfo(configStore, config);
    }

    // Processing user.
    if (ticket.processingUser?.firstName) {
      return {
        image: agentAvatar(ticket.processingUser),
        name: `${ticket.processingUser?.firstName}`,
        bot: false,
      };
    }

    // "Show AI and assignee": nobody assigned → the AI agent, never the team
    // stack or the last replying agent.
    if (headerMode === "ai-assignee") {
      return getBotOperatorInfo(configStore, config);
    }

    // Latest comment from agent.
    if (ticket.latestPublicComment?.user?.firstName) {
      return {
        image: agentAvatar(ticket.latestPublicComment.user),
        name: `${ticket.latestPublicComment.user?.firstName}`,
        bot: false,
      };
    }

    // Team fallback (also covers tickets whose latest public comment has no
    // agent user — bot/workflow auto-replies).
    return {
      image: null,
      name: config?.name ?? "Team",
      bot: false,
    };
  }

  return null;
};

// Optimistic header identity for a JUST-STARTED conversation: the create
// round-trip takes a moment, during which there is no ticket and
// getOperatorInfo returns null — the header rendered blank (or a skeleton)
// for 1-2s. A conversation a workflow picks up (started for one, or the
// project's conversation-start workflow) shows the bot immediately; everyone
// else gets the team fallback.
// (The team stack first and the bot after flashed other faces, e.g. Gleap
// staff in the customer's organisation, before the bot's avatar.)
export const getPendingOperatorInfo = (
  configStore: any,
  config: any,
  startedForBot: boolean,
) => {
  // "ai-only" / "ai-assignee" header modes never show the team stack, so
  // the AI agent is the right optimistic identity there too.
  const headerMode = config?.ticketHeaderMode ?? "ai-team";
  if (startedForBot || headerMode !== "ai-team") {
    return getBotOperatorInfo(configStore, config);
  }

  return {
    image: null,
    name: config?.name ?? "Team",
    bot: false,
  };
};

export const resetWidget = () => {
  useAppViewStore.getState().reset();
  useFormStore.getState().clearFeedbackFlowOptions();
  useRouterStore.getState().setPage("menu");
  useRouterStore.setState({ showBackButton: true });
};

export const closeWidgetAndReset = () => {
  CommunicationManager.getInstance().sendMessage({
    name: "close-widget",
  });

  resetWidget();
};

function WidgetHeader({
  headerOpacity = 1.0,
  scrolled = false,
  page,
}: {
  headerOpacity?: number;
  scrolled?: boolean;
  page?: string;
}) {
  const checklistStore = useChecklistStore();
  const configStore = useConfigStore();
  const formStore = useFormStore();
  const routerStore = useRouterStore();
  const appViewStore = useAppViewStore();
  const ticketStore = useTicketStore();
  const sessionStore = useSessionStore();
  const currentFeatureRequest = useFeatureRequestStore(
    (state) => state.currentFeatureRequest,
  );
  const currentHelpcenterTitle = useHelpCenterStore((state) =>
    state.getHelpcenterTitle(),
  );
  const currentCollection = useHelpCenterStore(
    (state) => state.currentCollection,
  );
  const currentArticle = useHelpCenterStore((state) => state.currentArticle);
  const helpCenterDomain = useHelpCenterStore((state) =>
    state.getHelpCenterDomain({ includeLanguage: true }),
  );
  const config = configStore.config;
  const sendingForm = formStore.sendingForm;
  const videoCallOpen = routerStore.openVideoCall;

  // The header renders the sub-header + background for the *routed page*. App
  // defers the routed body via useDeferredValue so the nav highlight can lead;
  // the header must follow that same deferred page, otherwise it snaps to the
  // new page's (white) sub-header while the old body is still on screen — the
  // "header changed but the rest isn't here yet" flash. Falls back to the live
  // page when rendered without the prop.
  const currentPage = page ?? routerStore.currentPage;

  const showBackButton = routerStore.showBackButton;
  const surveyTitle = useSurveyV2Store((s) => s.payload?.analytics?.name);

  const unreadTickets = ticketStore.unreadTickets;

  // A survey opened from a link inside the messenger: back returns to that page
  // (the store's close() flushes the answer and pops), X closes the widget.
  const renderSurveySubHeader = () => (
    <div className="widget-header-sub widget-header-sub-with-center">
      {showBackButton && routerStore.appMode === AppMode.WIDGET && (
        <IconButton
          icon={ChevronLeft}
          ariaLabel={configStore.translateTextWithFallback("back", "Back")}
          onClick={() => useSurveyV2Store.getState().close()}
        />
      )}
      <div className="widget-header-sub-center">
        <div className="widget-header-sub-title" role="heading" aria-level={1}>
          {surveyTitle || configStore.translateTextWithFallback("svSurvey", "Survey")}
        </div>
      </div>
      <ExpandWidgetButton />
      <IconButton
        icon={XIcon}
        ariaLabel={configStore.translateTextWithFallback("close", "Close")}
        onClick={() => {
          useSurveyV2Store.getState().reset();
          closeWidgetAndReset();
        }}
      />
    </div>
  );

  const renderFormSubHeader = () => {
    return (
      <div className="widget-header-sub widget-header-sub-with-center">
        {showBackButton &&
          !sendingForm &&
          routerStore.appMode === AppMode.WIDGET && (
            <IconButton
              icon={ChevronLeft}
              ariaLabel={configStore.translateTextWithFallback("back", "Back")}
              onClick={() => {
                if (formStore.currentStep > 0) {
                  formStore.showPrevStep();
                } else {
                  routerStore.popPage();
                  CommunicationManager.getInstance().sendMessage({
                    name: "cleanup-drawings",
                  });
                }
              }}
            />
          )}
        <div className="widget-header-sub-center">
          <div className="widget-header-sub-title" role="heading" aria-level={1}>
            {formStore.action?.title}
          </div>
        </div>
        <ExpandWidgetButton />
        {!sendingForm && routerStore.appMode !== AppMode.SURVEY_WEB && (
          <IconButton
            icon={XIcon}
            ariaLabel={configStore.translateTextWithFallback("close", "Close")}
            onClick={() => {
              closeWidgetAndReset();
            }}
          />
        )}
      </div>
    );
  };

  const renderConversationsSubHeader = () => {
    return (
      <div
        className={`widget-header-sub ${
          configStore.config.showConvFilters
            ? "widget-header-sub--noborder"
            : ""
        }`}
      >
        <div className="widget-header-sub-title widget-header-sub-title--no-back" role="heading" aria-level={1}>
          {configStore.config.allConversationsMenuLabel}
        </div>
        <ExpandWidgetButton />
        <IconButton
          icon={XIcon}
          ariaLabel={configStore.translateTextWithFallback("close", "Close")}
          onClick={() => {
            closeWidgetAndReset();
          }}
        />
      </div>
    );
  };

  // Who fronts the open conversation. While its ticket loads, a conversation
  // opened from the list shows the identity its list row showed, and a
  // just-started one the pending identity instead of a skeleton.
  const getConversationOperatorInfo = () => {
    const loadingShareToken = !ticketStore.currentTicket
      ? routerStore.currentData?.shareToken
      : undefined;
    const listTicket = loadingShareToken
      ? ticketStore.previousTickets.find(
          (ticket) => ticket.shareToken === loadingShareToken,
        )
      : undefined;

    return (
      getOperatorInfo(
        configStore,
        config,
        ticketStore.currentTicket ?? listTicket,
        ticketStore.currentComments,
      ) ??
      getPendingOperatorInfo(
        configStore,
        config,
        ticketStore.pendingConversationIsBot,
      )
    );
  };

  const renderConversationSubHeader = () => {
    const operatorInfo = getConversationOperatorInfo();
    // Use the conversation's team-effective hours config when present so the
    // header reflects the assigned team's online state, not the project's.
    const effectiveHoursConfig =
      ticketStore?.currentTicket?.effectiveHoursConfig;
    const { online, nextOpeningTime } = isOnline(effectiveHoursConfig);

    var replyTime = "";

    if (!configStore.config.showOnlineStatus) {
      replyTime = configStore.config.name ?? "";
    } else {
      if (!ticketStore.currentTicket && operatorInfo?.bot) {
        // Just-started conversation (ticket still being created) with the
        // optimistic bot identity — show the bot subtitle right away so the
        // text doesn't swap once the (type BOT) ticket lands.
        replyTime = configStore.translateText("botReplyTime");
      } else if (ticketStore.currentTicket) {
        if (ticketStore.currentTicket?.type === "BOT") {
          replyTime = configStore.translateText("botReplyTime");
        } else {
          if (online) {
            // Team-level replyTime (already localized) wins over project key.
            // If both are empty/unset (project's flowConfig.replyTime not
            // configured), render nothing — avoids ugly bare "." subtitle.
            const text =
              effectiveHoursConfig?.replyTime ??
              configStore.translateText(configStore.config.replyTime);
            replyTime = text && text.trim() !== "" ? `${text}.` : "";
          } else {
            const teamOoo =
              effectiveHoursConfig?.staticTranslations?.outOfOfficeMessage;
            if (teamOoo && teamOoo.trim() !== "") {
              replyTime = teamOoo;
            } else {
              const oooMessage =
                configStore.translateText("outOfOfficeMessage");
              if (
                oooMessage.trim() !== "" &&
                oooMessage !== "outOfOfficeMessage"
              ) {
                replyTime = configStore.translateText("outOfOfficeMessage");
              } else {
                replyTime = `${configStore.translateText("weAreBack")} ${configStore.formatRelativeTime(nextOpeningTime)}.`;
              }
            }
          }
        }
      }
    }

    const renderOperatorImage = () => {
      if (operatorInfo?.image) {
        return <div className="operator-info-image">{operatorInfo?.image}</div>;
      }

      if (configStore.team && configStore.team.length > 0) {
        const team = configStore.team.slice(0, 3);
        return (
          <div
            className="operator-info-team-image"
            style={{
              marginRight: `${10 + (team.length - 1) * 16}px`,
            }}
          >
            {team.map((teamUser, index) => {
              return (
                <AvatarImage
                  key={index}
                  src={teamUser.profileImageUrl}
                  alt={teamUser.firstName}
                />
              );
            })}
          </div>
        );
      }

      return null;
    };

    const operatorHeader = () => {
      if (!operatorInfo) {
        return (
          <>
            <div className="operator-info-content-image-loading"></div>
            <div className="operator-info-content-loading">
              <div className="operator-info-content-title-loading"></div>
              <div className="operator-info-content-subtitle-loading"></div>
            </div>
          </>
        );
      }

      // No reply time configured anywhere → fall back to the company name so
      // the header never shows a bare name without a second line (unless
      // that would just repeat the title, e.g. the team fallback).
      const subtitle =
        replyTime && replyTime.trim() !== ""
          ? replyTime
          : configStore.config.name && configStore.config.name !== operatorInfo.name
            ? configStore.config.name
            : "";

      return (
        <>
          {renderOperatorImage()}
          <div className="operator-info-content">
            {renderOperatorTitle(configStore, operatorInfo)}
            <div className="operator-info-content-subtitle">{subtitle}</div>
          </div>
        </>
      );
    };

    return (
      <div
        className={`widget-header-sub ${
          operatorInfo?.bot ? "widget-header-sub--bot" : ""
        } ${!showBackButton && "widget-header-sub--hide-back-button"}`}
      >
        {showBackButton && (
          <IconButton
            unreadCount={unreadTickets.length}
            icon={ChevronLeft}
            ariaLabel={configStore.translateTextWithFallback("back", "Back")}
            onClick={() => {
              routerStore.popPage();
            }}
          />
        )}
        <div className="widget-header-operator-info-container">
          <div className="widget-header-operator-info">{operatorHeader()}</div>
        </div>
        <ExpandWidgetButton />
        <IconButton
          icon={XIcon}
          ariaLabel={configStore.translateTextWithFallback("close", "Close")}
          onClick={() => {
            closeWidgetAndReset();
          }}
        />
      </div>
    );
  };

  const renderStatus = () => {
    const sts = config?.sts ?? false;

    if (
      !ticketStore.currentTicket?.type ||
      ticketStore.currentTicket?.type === "INQUIRY" ||
      ticketStore.currentTicket?.type === "BOT" ||
      !sts
    ) {
      return;
    }

    const currentStatus = ticketStore.currentTicket?.status;
    const stati = [
      {
        status: "OPEN",
        title: "submitted",
        isActive: true,
      },
      {
        status: "IN_PROGRESS",
        title: "inProgress",
        isActive: currentStatus !== "OPEN",
      },
      {
        status: "DONE",
        title: "resolved",
        isActive: currentStatus === "DONE",
      },
    ];

    return (
      <div className="widget-header-sub-content">
        <div className="widget-ticket-status">
          {stati.map((status, index) => {
            return (
              <div
                key={index}
                className={`widget-ticket-status-item ${
                  status.isActive ? "widget-ticket-status-item--active" : ""
                }`}
              >
                <div className="widget-ticket-status-item-bar"></div>
                <div className="widget-ticket-status-item-title">
                  {configStore.translateText(status.title)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderConversationSubHeaderTicket = () => {
    // Pending identity while the new conversation's ticket is being created
    // (this variant used to render NOTHING for those seconds).
    const operatorInfo = getConversationOperatorInfo();
    const effectiveHoursConfig =
      ticketStore?.currentTicket?.effectiveHoursConfig;
    const { online, nextOpeningTime } = isOnline(effectiveHoursConfig);

    var replyTime = "";

    if (!configStore.config.showOnlineStatus) {
      replyTime = configStore.config.name ?? "";
    } else {
      if (!ticketStore.currentTicket && operatorInfo?.bot) {
        // Pending bot identity (see renderConversationSubHeader).
        replyTime = configStore.translateText("botReplyTime");
      } else if (ticketStore.currentTicket) {
        if (online) {
          const text =
            effectiveHoursConfig?.replyTime ??
            configStore.translateText(configStore.config.replyTime);
          replyTime = text && text.trim() !== "" ? `${text}.` : "";
        } else {
          const teamOoo =
            effectiveHoursConfig?.staticTranslations?.outOfOfficeMessage;
          if (teamOoo && teamOoo.trim() !== "") {
            replyTime = teamOoo;
          } else {
            const oooMessage = configStore.translateText("outOfOfficeMessage");
            if (
              oooMessage.trim() !== "" &&
              oooMessage !== "outOfOfficeMessage"
            ) {
              replyTime = configStore.translateText("outOfOfficeMessage");
            } else {
              replyTime = `${configStore.translateText("weAreBack")} ${configStore.formatRelativeTime(nextOpeningTime)}.`;
            }
          }
        }
      }
    }

    const renderOperatorImage = () => {
      if (operatorInfo?.image) {
        return <div className="operator-info-image">{operatorInfo?.image}</div>;
      }

      if (configStore.team && configStore.team.length > 0) {
        const team = configStore.team.slice(0, 3);
        return (
          <div
            className="operator-info-team-image"
            style={{
              marginRight: `${10 + (team.length - 1) * 16}px`,
            }}
          >
            {team.map((teamUser, index) => {
              return (
                <AvatarImage
                  key={index}
                  src={teamUser.profileImageUrl}
                  alt={teamUser.firstName}
                />
              );
            })}
          </div>
        );
      }

      return null;
    };

    const operatorHeader = () => {
      if (!operatorInfo) {
        return <></>;
      }

      // Same company-name fallback as renderConversationSubHeader.
      const subtitle =
        replyTime && replyTime.trim() !== ""
          ? replyTime
          : configStore.config.name && configStore.config.name !== operatorInfo.name
            ? configStore.config.name
            : "";

      return (
        <>
          {renderOperatorImage()}
          <div className="operator-info-content">
            {renderOperatorTitle(configStore, operatorInfo)}
            <div className="operator-info-content-subtitle">{subtitle}</div>
          </div>
        </>
      );
    };

    return (
      <div className="widget-header-sub widget-header-sub-ticket widget-header-sub-center">
        <div className="widget-header-sub-header">
          {showBackButton && (
            <IconButton
              unreadCount={unreadTickets.length}
              icon={ChevronLeft}
              ariaLabel={configStore.translateTextWithFallback("back", "Back")}
              onClick={() => {
                routerStore.popPage();
              }}
            />
          )}
          <div className="widget-header-operator-info-container">
            <div className="widget-header-operator-info">
              {operatorHeader()}
            </div>
          </div>
          <ExpandWidgetButton />
          <IconButton
            icon={XIcon}
            ariaLabel={configStore.translateTextWithFallback("close", "Close")}
            onClick={() => {
              closeWidgetAndReset();
            }}
          />
        </div>
        {renderStatus()}
      </div>
    );
  };

  const renderNewsSubHeader = () => {
    return (
      <div className="widget-header-sub">
        <div className="widget-header-sub-title widget-header-sub-title--no-back" role="heading" aria-level={1}>
          {configStore.config.newsMenuLabel}
        </div>
        <ExpandWidgetButton />
        <IconButton
          icon={XIcon}
          ariaLabel={configStore.translateTextWithFallback("close", "Close")}
          onClick={() => {
            closeWidgetAndReset();
          }}
        />
      </div>
    );
  };

  const renderFeatureRequestDetailsHeader = () => {
    return (
      <div className="widget-header-sub widget-header-sub-ticket widget-header-sub-center">
        <div className="widget-header-sub-header">
          <IconButton
            icon={ChevronLeft}
            ariaLabel={configStore.translateTextWithFallback("back", "Back")}
            onClick={() => {
              routerStore.popPage();
            }}
          />
          {/* No title here — the detail view renders it directly below,
              and the truncated duplicate read as clutter. The empty element
              keeps the back/X buttons pushed to the edges. */}
          <div className="widget-header-sub-newtitle" />
          <ExpandWidgetButton />
          <IconButton
            icon={XIcon}
            ariaLabel={configStore.translateTextWithFallback("close", "Close")}
            onClick={() => {
              closeWidgetAndReset();
            }}
          />
        </div>
      </div>
    );
  };

  const renderChecklistSubHeader = () => {
    return (
      <div className="widget-header-sub">
        <div className="widget-header-sub-title widget-header-sub-title--no-back" role="heading" aria-level={1}>
          {configStore.config.checklistMenuLabel}
        </div>
        <ExpandWidgetButton />
        <IconButton
          icon={XIcon}
          ariaLabel={configStore.translateTextWithFallback("close", "Close")}
          onClick={() => {
            closeWidgetAndReset();
          }}
        />
      </div>
    );
  };

  const renderChecklistDetailsHeader = () => {
    return (
      <div className="widget-header-sub">
        {checklistStore.checklists?.length > 1 ? (
          <IconButton
            icon={ChevronLeft}
            ariaLabel={configStore.translateTextWithFallback("back", "Back")}
            onClick={() => {
              routerStore.popPage();
            }}
          />
        ) : (
          <div className="close-spacer"></div>
        )}
        <div className={`widget-header-sub-title`} role="heading" aria-level={1}>
          {configStore.config.checklistMenuLabel}
        </div>
        <ExpandWidgetButton />
        <IconButton
          icon={XIcon}
          ariaLabel={configStore.translateTextWithFallback("close", "Close")}
          onClick={() => {
            closeWidgetAndReset();
          }}
        />
      </div>
    );
  };

  const renderRoadmapSubHeader = () => {
    return (
      <div className="widget-header-sub">
        <div className="widget-header-sub-title widget-header-sub-title widget-header-sub-title--no-back" role="heading" aria-level={1}>
          {configStore.config.featureRequestsMenuLabel}
        </div>
        <ExpandWidgetButton />
        <IconButton
          icon={XIcon}
          ariaLabel={configStore.translateTextWithFallback("close", "Close")}
          onClick={() => {
            closeWidgetAndReset();
          }}
        />
      </div>
    );
  };

  const renderHelpCenterSubHeader = () => {
    return (
      <div className="widget-header-sub widget-header-sub--helpcenter">
        <div className="widget-header-sub-title widget-header-sub-title--no-back" role="heading" aria-level={1}>
          {currentHelpcenterTitle}
        </div>
        <ExpandWidgetButton />
        <IconButton
          icon={XIcon}
          ariaLabel={configStore.translateTextWithFallback("close", "Close")}
          onClick={() => {
            closeWidgetAndReset();
          }}
        />
      </div>
    );
  };

  const renderHelpCenterArticleHeader = () => {
    const article = currentArticle?.article ?? currentArticle;
    const articleId = article?.docId ?? article?.id;
    const requestedId = routerStore.currentData?.id;
    const title =
      articleId && articleId === requestedId ? (article?.title ?? "") : "";
    const pageShareUrl = buildHelpCenterArticleUrl({
      domain: helpCenterDomain,
      articleId,
      title: article?.title,
      url: article?.url,
      session: sessionStore?.session,
    });

    return (
      <div className="widget-header-sub">
        {showBackButton && (
          <IconButton
            icon={ChevronLeft}
            ariaLabel={configStore.translateTextWithFallback("back", "Back")}
            onClick={() => {
              routerStore.popPage();
            }}
          />
        )}
        <div className="widget-header-sub-title" role="heading" aria-level={1}>{title}</div>
        {article && pageShareUrl && (
          <IconButton
            icon={ShareIcon}
            ariaLabel={configStore.translateTextWithFallback("share", "Share")}
            className="widget-header-sub-share-button"
            onClick={() => {
              Communicator.openURL(pageShareUrl, true);
            }}
          />
        )}
        <ExpandWidgetButton />
        <IconButton
          icon={XIcon}
          ariaLabel={configStore.translateTextWithFallback("close", "Close")}
          onClick={() => {
            closeWidgetAndReset();
          }}
        />
      </div>
    );
  };

  const renderHelpCenterDetailHeader = () => {
    let title = currentCollection?.collection?.title ?? currentHelpcenterTitle;
    if (currentPage === "helpcenter-search") {
      return null;
    }

    return (
      <div className="widget-header-sub">
        {showBackButton && (
          <IconButton
            icon={ChevronLeft}
            ariaLabel={configStore.translateTextWithFallback("back", "Back")}
            onClick={() => {
              routerStore.popPage();
            }}
          />
        )}
        <div className="widget-header-sub-title" role="heading" aria-level={1}>{title}</div>
        <ExpandWidgetButton />
        <IconButton
          icon={XIcon}
          ariaLabel={configStore.translateTextWithFallback("close", "Close")}
          onClick={() => {
            closeWidgetAndReset();
          }}
        />
      </div>
    );
  };

  const renderAppViewHeader = () => {
    const showBack =
      (appViewStore.pageStack.length > 1 || routerStore.history.length > 1) &&
      showBackButton;

    return (
      <div className="widget-header-sub widget-header-sub--noborder">
        {showBack && (
          <IconButton
            icon={ChevronLeft}
            ariaLabel={configStore.translateTextWithFallback("back", "Back")}
            onClick={() => {
              if (appViewStore.pageStack.length > 1) {
                appViewStore.popPage();
              } else {
                routerStore.popPage();
              }
            }}
          />
        )}
        <div
          className={`widget-header-sub-title ${
            !showBack && "widget-header-sub-title--no-back"
          }`}
        >
          <div className={`widget-header-sub-title`} role="heading" aria-level={1}>
            {configStore.translateText(appViewStore.getCurrentPage())}
          </div>
        </div>
        <ExpandWidgetButton />
        <IconButton
          icon={XIcon}
          ariaLabel={configStore.translateTextWithFallback("close", "Close")}
          onClick={() => {
            closeWidgetAndReset();
          }}
        />
      </div>
    );
  };

  const renderAppExtendedViewHeader = () => {
    const showBack =
      (appViewStore.pageStack.length > 1 || routerStore.history.length > 1) &&
      showBackButton;

    const pageShareUrl = withSessionIdentity(
      appViewStore.getCurrentPageUrl() ?? "",
      sessionStore?.session,
    );

    return (
      <div
        className={`widget-header-sub widget-header-sub--extended ${
          appViewStore.showExtendedViewTitle &&
          "widget-header-sub--extended--scrolled"
        } ${!showBack && "widget-header-sub--hide-back-button"}`}
      >
        {showBack && (
          <IconButton
            icon={ChevronLeft}
            ariaLabel={configStore.translateTextWithFallback("back", "Back")}
            onClick={() => {
              if (appViewStore.pageStack.length > 1) {
                appViewStore.popPage();
              } else {
                routerStore.popPage();
              }
            }}
          />
        )}
        <div className={`widget-header-sub-title`} role="heading" aria-level={1}>
          {configStore.translateText(appViewStore.getCurrentPage())}
        </div>
        {pageShareUrl && pageShareUrl.length > 0 && (
          <IconButton
            icon={ShareIcon}
            ariaLabel={configStore.translateTextWithFallback("share", "Share")}
            className="widget-header-sub-share-button"
            onClick={() => {
              Communicator.openURL(pageShareUrl, true);
            }}
          />
        )}
        <ExpandWidgetButton />
        <IconButton
          icon={XIcon}
          ariaLabel={configStore.translateTextWithFallback("close", "Close")}
          onClick={() => {
            closeWidgetAndReset();
          }}
        />
      </div>
    );
  };

  const renderHeader = () => {
    if (currentPage === "menu") {
      return null;
    }
    if (currentPage === "flow") {
      return renderFormSubHeader();
    }
    if (currentPage === "survey-inline") {
      return renderSurveySubHeader();
    }
    if (currentPage === "conversations") {
      return renderConversationsSubHeader();
    }
    if (
      currentPage === "conversation" ||
      currentPage === "conversationloading"
    ) {
      if (
        !ticketStore.currentTicket?.type ||
        ticketStore.currentTicket?.type === "BOT" ||
        ticketStore.currentTicket?.type === "INQUIRY"
      ) {
        return renderConversationSubHeader();
      }
      return renderConversationSubHeaderTicket();
    }
    if (currentPage === "news") {
      return renderNewsSubHeader();
    }
    if (currentPage === "roadmap") {
      return renderRoadmapSubHeader();
    }
    if (currentPage === "feature-request-details") {
      return renderFeatureRequestDetailsHeader();
    }
    if (currentPage === "checklists") {
      return renderChecklistSubHeader();
    }
    if (currentPage === "checklistdetails") {
      return renderChecklistDetailsHeader();
    }
    if (currentPage === "helpcenter") {
      return renderHelpCenterSubHeader();
    }
    if (currentPage === "helpcenter-article") {
      return renderHelpCenterArticleHeader();
    }
    if (
      currentPage === "helpcenter-collection" ||
      currentPage === "helpcenter-search"
    ) {
      return renderHelpCenterDetailHeader();
    }
    if (currentPage === "appview") {
      if (appViewStore.extendedView) {
        return renderAppExtendedViewHeader();
      }
      return renderAppViewHeader();
    }
    return null;
  };

  if (videoCallOpen) {
    return null;
  }

  return (
    <div
      role="banner"
      className={`widget-header ${scrolled && "widget-header--scrolled"} ${
        currentPage === "menu" && "widget-header--bg"
      } ${
        currentPage === "conversation" &&
        ticketStore.currentTicket?.type === "BUG" &&
        "widget-header--bglarge"
      }`}
    >
      {renderHeader()}
    </div>
  );
}

export default WidgetHeader;
